// ✅ src/pages/VehicleV2/TotalWorkingHrsView_Print.jsx
// PRINT PAGE for: TotalWorkingHrsView.aspx (B-39/1)
// ✅ Matches your screenshot layout:
//   - Nissi Infotech header box
//   - Title: "Total Working HRS / KM Report"
//   - Meta block: Equipment, Reg No, Code, Store, Branch, Type, Sub Type
//   - Grid: Date | Starting Hrs | Closing Hrs | Total Working Hrs | Starting KM | Closing KM | Total KM | Site Name
//   - Footer totals: Total Hrs, Total KM
//
// ✅ Reads from URL:
//   vehicleId  (required)  (ASPX uses Session["VehTPIdView"] = vehicleid)
//   fromDate, toDate, branch, store, vehicleMainType (optional if you want)
//
// ✅ Backend expected (you can implement either one):
//   Option A) GET /api/totalWorkingHrsRpt/view?vehicleId=123&fromDate=dd/MM/yyyy&todate=dd/MM/yyyy
//      -> { success:true, header:{...}, rows:[...], totals:{ totalHrs, totalKm } }
//   Option B) GET /api/totalWorkingHrsRpt/view?vehicleId=123
//      -> same response
//
// If your backend currently returns only rows, this component still works:
// - it derives header fields where possible
// - totals are computed from rows

import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";

const API_BASE = "https://nissi-india.com/api/totalWorkingHrRptView";

/* ---------------- helpers ---------------- */
const toStr = (v) => String(v ?? "").trim();

const pad2 = (n) => String(n).padStart(2, "0");
const fmtDate = (val) => {
  const s = toStr(val);
  if (!s) return "";
  // already dd/MM/yyyy
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) return s;

  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
};

const toNum = (v) => {
  const n = Number(String(v ?? "").replace(/,/g, ""));
  return Number.isFinite(n) ? n : 0;
};

const toFixed2 = (v) => {
  const n = toNum(v);
  return n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const sum = (arr) => arr.reduce((a, b) => a + toNum(b), 0);

/* ---------------- normalize row ----------------
  Try multiple possible DB field names (because we don't see your exact proc)
*/
function normalizeDetailRow(r) {
  return {
    date: fmtDate(r?.ProgressDate ?? r?.fld_Progressentrydate ?? r?.date ?? r?.Date),
    startingHrs: toFixed2(r?.StartingHrs ?? r?.fld_statrtinghrs ?? r?.startinghrs ?? r?.startHrs ?? 0),
    closingHrs: toFixed2(r?.ClosingHrs ?? r?.fld_closinghrs ?? r?.closinghrs ?? r?.closeHrs ?? 0),
    totalWorkingHrs: toFixed2(
      r?.TotalWorkingHrs ??
        r?.TotalWorkingHrsValue ??
        r?.totalworkinghrs ??
        r?.totalHrs ??
        // if backend already provides daily working hrs:
        r?.fld_workinghrs ??
        0
    ),
    startingKm: toFixed2(r?.StartingKM ?? r?.fld_startingkilometer ?? r?.startingkm ?? r?.startKm ?? 0),
    closingKm: toFixed2(r?.ClosingKM ?? r?.fld_closingkilometer ?? r?.closingkm ?? r?.closeKm ?? 0),
    totalKm: toFixed2(r?.TotalKM ?? r?.TotalKms ?? r?.totalkm ?? r?.totalKm ?? 0),
    siteName: toStr(r?.SiteName ?? r?.sitename ?? r?.fld_sitename ?? ""),
    _raw: r,
  };
}

export default function TotalWorkingHrsView_Print() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [header, setHeader] = useState(null);
  const [rows, setRows] = useState([]);

  const q = useMemo(() => {
    const get = (k, d = "") => toStr(searchParams.get(k) ?? d);
    return {
      vehicleId: get("vehicleId", ""), // required
      fromDate: get("fromDate", ""),
      toDate: get("toDate", ""),
      branch: get("branch", ""),
      store: get("store", ""),
      vehicleMainType: get("vehicleMainType", ""),
    };
  }, [searchParams]);

  const apiUrl = useMemo(() => {
    const p = new URLSearchParams();
    p.set("vehicleId", q.vehicleId || "");

    // optional filters (if your view proc supports them)
    if (q.fromDate) p.set("fromDate", q.fromDate);
    if (q.toDate) p.set("toDate", q.toDate);

    return `${API_BASE}/view?${p.toString()}`;
  }, [q.vehicleId, q.fromDate, q.toDate]);

  useEffect(() => {
    let alive = true;

    const load = async () => {
      try {
        if (!q.vehicleId) {
          setErr("Missing vehicleId in URL");
          setRows([]);
          setHeader(null);
          setLoading(false);
          return;
        }

        setLoading(true);
        setErr("");

        const res = await axios.get(apiUrl);
        if (!res?.data?.success) throw new Error(res?.data?.message || "Failed to load report");

        const h = res?.data?.header || null;
        const raw = res?.data?.rows || res?.data?.details || [];

        if (!alive) return;

        setHeader(h);
        setRows(raw);
      } catch (e) {
        if (!alive) return;
        setHeader(null);
        setRows([]);
        setErr(e?.response?.data?.message || e.message || "Failed to load report");
      } finally {
        if (alive) setLoading(false);
      }
    };

    load();
    return () => {
      alive = false;
    };
  }, [apiUrl, q.vehicleId]);

  const normalized = useMemo(() => rows.map(normalizeDetailRow), [rows]);

  const totals = useMemo(() => {
    // if backend provides totals use those, else compute
    const backendTotalHrs = header?.totalHrs ?? header?.TotalHrs ?? null;
    const backendTotalKm = header?.totalKm ?? header?.TotalKM ?? header?.TotalKms ?? null;

    const computedTotalHrs = sum(normalized.map((r) => r.totalWorkingHrs));
    const computedTotalKm = sum(normalized.map((r) => r.totalKm));

    return {
      totalHrs: backendTotalHrs !== null && backendTotalHrs !== undefined ? toFixed2(backendTotalHrs) : toFixed2(computedTotalHrs),
      totalKm: backendTotalKm !== null && backendTotalKm !== undefined ? toFixed2(backendTotalKm) : toFixed2(computedTotalKm),
    };
  }, [normalized, header]);

  // Header fields (prefer backend header, else derive from first row)
  const viewMeta = useMemo(() => {
    const first = normalized?.[0]?._raw || {};

    return {
      equipment: toStr(header?.equipment ?? header?.vehicle_name ?? first?.vehicle_name ?? first?.Equipment ?? ""),
      regNo: toStr(header?.regNo ?? header?.vehicle_regno ?? first?.vehicle_regno ?? first?.RegNo ?? ""),
      code: toStr(header?.code ?? header?.vehicle_code ?? first?.vehicle_code ?? first?.Code ?? ""),
      store: toStr(header?.store ?? header?.fld_storename ?? first?.fld_storename ?? first?.Store ?? ""),
      branch: toStr(header?.branch ?? header?.BranchName ?? first?.BranchName ?? ""),
      type: toStr(header?.type ?? header?.vehicletype ?? first?.vehicletype ?? ""),
      subType: toStr(header?.subType ?? header?.subtype ?? first?.subtype ?? ""),
    };
  }, [header, normalized]);

  return (
    <div className="w-full bg-white print:max-w-none print:mx-0">
      <style>{`
        @page { size: A4 portrait; margin: 0.35in; }

        @media print {
          body { margin: 0; padding: 0; font-size: 9pt; }
          .no-print { display: none !important; }
          table { border-collapse: collapse; width: 100%; }
          th, td { border: 1px solid #000 !important; padding: 3px 4px !important; }
        }

        .box { border: 2px solid #000; }
        .thin { border: 1px solid #000; }
        .t { border-collapse: collapse; width: 100%; }
        .t th, .t td { border: 1px solid #000; }
      `}</style>

      <div className="p-2">
        {/* actions */}
        <div className="no-print flex items-center justify-between mb-3">
          <button onClick={() => navigate(-1)} className="px-4 py-2 border rounded hover:bg-gray-50">
            ← Back
          </button>
          <button onClick={() => window.print()} className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded shadow">
            🖨️ Print
          </button>
        </div>

        {loading ? (
          <div className="no-print p-3 border rounded bg-gray-50 text-sm mb-2">Loading report...</div>
        ) : err ? (
          <div className="no-print p-3 border rounded bg-red-50 text-sm text-red-700 mb-2">{err}</div>
        ) : null}

        {/* HEADER BOX (like screenshot) */}
        <div className="box mb-2">
          <div className="flex items-center p-2">
            {/* You can replace this with your logo image if you have it */}
            <div className="w-24 h-16 thin flex items-center justify-center mr-3 bg-gray-100">
              <div className="text-[10px] font-bold text-center leading-tight">
                NISSI
                <br />
                INFOTECH
              </div>
            </div>

            <div className="flex-1 text-center leading-tight">
              <div className="text-lg font-bold">Nissi Infotech</div>
              <div className="text-sm font-semibold">Nissi</div>
              <div className="text-sm font-semibold">CIVIL CONSTRUCTION</div>
              <div className="text-xs">
                3/42-12, Nagappa colony, kavundampalayam-641030, Tamil Nadu, India.
              </div>
              <div className="text-xs">
                Phone No : 9003744644 &nbsp;&nbsp;&nbsp;&nbsp; infracnsoft-implementation@nissiinfotech.in
              </div>
            </div>
          </div>
        </div>

        {/* TITLE */}
        <div className="box text-center py-1 mb-2">
          <div className="text-base font-bold underline">Total Working HRS / KM Report</div>
        </div>

        {/* META BLOCK (2 columns like screenshot) */}
        <div className="box p-2 mb-2 text-xs">
          <div className="grid grid-cols-2 gap-x-10 gap-y-2">
            <div className="space-y-2">
              <div>
                <b>Equipment</b> &nbsp;:&nbsp; {viewMeta.equipment || "-"}
              </div>
              <div>
                <b>Reg No</b> &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp; {viewMeta.regNo || "-"}
              </div>
              <div>
                <b>Code</b> &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp; {viewMeta.code || "-"}
              </div>
              <div>
                <b>Store</b> &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp; {viewMeta.store || "-"}
              </div>
            </div>

            <div className="space-y-2">
              <div>
                <b>Branch</b> &nbsp;:&nbsp; {viewMeta.branch || "-"}
              </div>
              <div>
                <b>Type</b> &nbsp;&nbsp;&nbsp;&nbsp;:&nbsp; {viewMeta.type || "-"}
              </div>
              <div>
                <b>Sub Type</b> :&nbsp; {viewMeta.subType || "-"}
              </div>
              <div>
                <b>INFRA SCREEN</b> :&nbsp; B-39/1
              </div>
            </div>
          </div>
        </div>

        {/* GRID */}
        <div className="box p-0 mb-2">
          <table className="t text-xs">
            <thead>
              <tr className="bg-gray-100">
                <th className="text-center whitespace-nowrap">Date</th>
                <th className="text-center whitespace-nowrap">Starting Hrs</th>
                <th className="text-center whitespace-nowrap">Closing Hrs</th>
                <th className="text-center whitespace-nowrap">Total Working Hrs</th>
                <th className="text-center whitespace-nowrap">Starting KM</th>
                <th className="text-center whitespace-nowrap">Closing KM</th>
                <th className="text-center whitespace-nowrap">Total KM</th>
                <th className="text-center whitespace-nowrap">Site Name</th>
              </tr>
            </thead>

            <tbody>
              {normalized.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-2">
                    No Records Found
                  </td>
                </tr>
              ) : (
                normalized.map((r, idx) => (
                  <tr key={`${r.date}-${idx}`}>
                    <td className="text-center whitespace-nowrap">{r.date}</td>
                    <td className="text-right whitespace-nowrap">{r.startingHrs}</td>
                    <td className="text-right whitespace-nowrap">{r.closingHrs}</td>
                    <td className="text-right whitespace-nowrap">{r.totalWorkingHrs}</td>
                    <td className="text-right whitespace-nowrap">{r.startingKm}</td>
                    <td className="text-right whitespace-nowrap">{r.closingKm}</td>
                    <td className="text-right whitespace-nowrap">{r.totalKm}</td>
                    <td className="text-left">{r.siteName}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* TOTALS FOOTER (like screenshot bottom line totals) */}
        <div className="box p-2 text-xs">
          <div className="flex items-center justify-center gap-20">
            <div className="flex items-center gap-3">
              <b>Total Hrs</b>
              <span className="inline-block min-w-[80px] text-right border-b border-black">{totals.totalHrs}</span>
            </div>

            <div className="flex items-center gap-3">
              <b>Total KM</b>
              <span className="inline-block min-w-[80px] text-right border-b border-black">{totals.totalKm}</span>
            </div>
          </div>
        </div>

        {/* FOOTER LINE */}
        <div className="mt-2 thin p-1 text-[10px] text-center">
          Report generated by ERP-Software - www.infracnsoft.com owned by www.nissiinfotech.com
        </div>
      </div>
    </div>
  );
}
